// Template import modal for CSV-based bulk template creation
// CHANGE: Removed all debug console statements for production
// Handles file upload, validation, preview, and batch import of templates with items and recurrence
// CHANGE: Created for template CSV import feature
// CHANGE: Updated to use readCSVFileWithEncoding for proper accent handling

import { createPortal } from 'react-dom';
import { getPortalRoot } from '../../lib/portalUtils';
import { X, Upload, Download, AlertCircle, CheckCircle, XCircle, Loader } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useToast } from '../../hooks/useToast';
import {
  parseTemplateCSV,
  importTemplates,
  downloadTemplateCSVTemplate,
  type CSVTemplateRow,
} from '../../lib/csvImportTemplates';
import { getServices } from '../../lib/services';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import { TemplateImportResultModal } from './TemplateImportResultModal';
import { readCSVFileWithEncoding } from '../../lib/csvEncoding';
import { ProgressBar } from '../ui/ProgressBar';

interface TemplateImportModalProps {
  onClose: () => void;
  onImportComplete: () => void;
}

type ImportStep = 'upload' | 'preview' | 'importing';

export interface TemplateImportResult {
  templateName: string;
  itemCount: number;
  recurrence: string;
  error?: string;
}

export function TemplateImportModal({ onClose, onImportComplete }: TemplateImportModalProps) {
  const [step, setStep] = useState<ImportStep>('upload');
  const [csvFile, setCSVFile] = useState<File | null>(null);
  const [parsedTemplates, setParsedTemplates] = useState<CSVTemplateRow[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [parseWarnings, setParseWarnings] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importResults, setImportResults] = useState<TemplateImportResult[] | null>(null);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [services, setServices] = useState<Array<{ id: string; name: string }>>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();

  const validTemplates = parsedTemplates.filter(t => t.isValid);
  const invalidTemplates = parsedTemplates.filter(t => !t.isValid);

  // Load services on mount (modal is unmounted/remounted on each open, ensuring fresh data)
  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    const result = await getServices();
    if (result.success && result.data) {
      setServices(result.data.map(s => ({ id: s.id, name: s.name })));
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !importing) {
        handleCloseAttempt();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [importing]);

  const handleCloseAttempt = () => {
    if (step !== 'upload' && !importing) {
      setShowCloseConfirmation(true);
    } else {
      onClose();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !importing) {
      handleCloseAttempt();
    }
  };

  const handleFileSelect = async (file: File) => {
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showToast('Le fichier est trop volumineux (max 5MB)', 'error');
      return;
    }

    // Check file type
    if (!file.name.endsWith('.csv')) {
      showToast('Seuls les fichiers CSV sont acceptés', 'error');
      return;
    }

    setCSVFile(file);

    try {
      // Use encoding-aware reader to properly handle accents
      const content = await readCSVFileWithEncoding(file);
      const result = await parseTemplateCSV(content, services);

      if (result.errors.length > 0) {
        setParseErrors(result.errors);
        showToast(result.errors[0], 'error');
        return;
      }

      setParsedTemplates(result.templates);
      setParseErrors([]);
      setParseWarnings(result.warnings);

      if (result.templates.length === 0) {
        showToast('Aucun template valide trouvé dans le fichier', 'error');
        return;
      }

      if (result.warnings.length > 0) {
        showToast(result.warnings[0], 'warning');
      }

      setStep('preview');
    } catch (error) {
      showToast('Erreur lors de la lecture du fichier CSV', 'error');
      setParseErrors(['Erreur lors de la lecture du fichier. Vérifiez le format.']);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleStartImport = async () => {
    if (validTemplates.length === 0) {
      showToast('Aucun template valide à importer', 'error');
      return;
    }

    setStep('importing');
    setImporting(true);
    setImportProgress({ current: 0, total: validTemplates.length });

    try {
      const result = await importTemplates(validTemplates, services);

      // Create results array
      const results: TemplateImportResult[] = validTemplates.map(template => {
        const error = result.errors.find(e => e.templateName === template.finalName);

        let recurrenceText = 'Aucune';
        if (template.recurrenceFrequency === 'daily') {
          recurrenceText = 'Quotidienne';
        } else if (template.recurrenceFrequency === 'weekly') {
          const days = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
          const dayNames = template.recurrenceDays.map(d => days[d - 1]).join(', ');
          recurrenceText = `Hebdo (${dayNames})`;
        } else if (template.recurrenceFrequency === 'monthly') {
          recurrenceText = `Mensuelle (jour ${template.recurrenceDayOfMonth})`;
        }

        return {
          templateName: template.finalName,
          itemCount: template.items.length,
          recurrence: recurrenceText,
          error: error?.error
        };
      });

      setImportResults(results);
      setImporting(false);

      if (result.success > 0) {
        showToast(
          `${result.success} template${result.success > 1 ? 's' : ''} importé${result.success > 1 ? 's' : ''}${result.errors.length > 0 ? ` (${result.errors.length} erreur${result.errors.length > 1 ? 's' : ''})` : ''}`,
          result.errors.length > 0 ? 'warning' : 'success'
        );
      } else {
        showToast('Aucun template n\'a pu être importé', 'error');
      }
    } catch (error) {
      showToast('Erreur lors de l\'import', 'error');
      setImporting(false);
    }
  };

  const formatRecurrence = (template: CSVTemplateRow): string => {
    if (!template.recurrenceFrequency) return 'Aucune';

    if (template.recurrenceFrequency === 'daily') {
      return 'Quotidienne';
    }

    if (template.recurrenceFrequency === 'weekly') {
      const days = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
      const dayNames = template.recurrenceDays.map(d => days[d - 1]).join(', ');
      return `Hebdo (${dayNames})`;
    }

    if (template.recurrenceFrequency === 'monthly') {
      return `Mensuelle (jour ${template.recurrenceDayOfMonth})`;
    }

    return 'Personnalisée';
  };

  if (importResults) {
    return (
      <TemplateImportResultModal
        results={importResults}
        onClose={() => {
          setImportResults(null);
          onClose();
        }}
        onImportComplete={onImportComplete}
      />
    );
  }

  return createPortal(
    <>
      <div
        className="modal-overlay"
        onClick={handleOverlayClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-modal-title"
      >
        <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100 shrink-0">
            <div>
              <h3 id="import-modal-title" className="text-xl font-bold text-gray-900">
                Importer des templates
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {step === 'upload' && 'Téléchargez un fichier CSV contenant les templates'}
                {step === 'preview' && `${validTemplates.length} template${validTemplates.length > 1 ? 's' : ''} prêt${validTemplates.length > 1 ? 's' : ''} à importer`}
                {step === 'importing' && 'Import en cours...'}
              </p>
            </div>
            {!importing && (
              <button
                onClick={handleCloseAttempt}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {step === 'upload' && (
              <div className="space-y-6">
                {/* Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Format du fichier CSV</h4>
                  <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                    <li>
                      <strong>Format multi-lignes:</strong> Une ligne template suivie de lignes items
                    </li>
                    <li>
                      <strong>Colonnes template:</strong> template_name, description, estimated_duration, service, recurrence_frequency, recurrence_days, recurrence_day_of_month, task_title
                    </li>
                    <li>
                      <strong>Colonnes items:</strong> task_title (autres colonnes vides)
                    </li>
                    <li>
                      <strong>Séparateur:</strong> Détection automatique (virgule, point-virgule ou tabulation)
                    </li>
                    <li>
                      <strong>Services:</strong> Doivent exister dans la base (pas de création automatique)
                    </li>
                    <li>
                      <strong>Récurrence:</strong> daily, weekly (avec jours 1-7), monthly (avec jour 1-31)
                    </li>
                    <li>
                      <strong>Doublons:</strong> Renommage automatique avec suffixe " (2)", " (3)", etc.
                    </li>
                    <li>
                      <strong>Limite:</strong> Maximum 100 templates par fichier
                    </li>
                  </ul>
                </div>

                {/* Template Download */}
                <div className="flex justify-center">
                  <button
                    onClick={downloadTemplateCSVTemplate}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-all duration-200"
                  >
                    <Download size={20} />
                    <span>Télécharger le modèle CSV</span>
                  </button>
                </div>

                {/* Drop Zone */}
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-primary-500 transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-lg font-semibold text-gray-700 mb-2">
                    Glissez-déposez votre fichier CSV ici
                  </p>
                  <p className="text-sm text-gray-500 mb-4">ou cliquez pour parcourir</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                  {csvFile && (
                    <p className="text-sm text-primary-600 font-medium">
                      Fichier sélectionné: {csvFile.name}
                    </p>
                  )}
                </div>

                {/* Parse Errors */}
                {parseErrors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <XCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                      <div className="flex-1">
                        <p className="font-semibold text-red-900 mb-2">Erreurs détectées</p>
                        <ul className="text-sm text-red-800 space-y-1">
                          {parseErrors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 'preview' && (
              <div className="space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="text-green-600" size={24} />
                      <div>
                        <p className="text-sm text-green-800 font-medium">Templates valides</p>
                        <p className="text-2xl font-bold text-green-900">{validTemplates.length}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <XCircle className="text-red-600" size={24} />
                      <div>
                        <p className="text-sm text-red-800 font-medium">Erreurs</p>
                        <p className="text-2xl font-bold text-red-900">{invalidTemplates.length}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Warnings */}
                {parseWarnings.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                      <div className="flex-1">
                        <p className="font-semibold text-yellow-900 mb-2">Avertissements</p>
                        <ul className="text-sm text-yellow-800 space-y-1">
                          {parseWarnings.map((warning, index) => (
                            <li key={index}>{warning}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Preview Table */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Aperçu des templates</h4>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto max-h-96">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                          <tr>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">
                              Ligne
                            </th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">
                              Nom du template
                            </th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">
                              Items
                            </th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">
                              Service
                            </th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">
                              Récurrence
                            </th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">
                              Statut
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {parsedTemplates.map((template, index) => (
                            <tr
                              key={index}
                              className={template.isValid ? 'bg-white hover:bg-gray-50' : 'bg-red-50'}
                            >
                              <td className="px-4 py-3 text-sm text-gray-700">{template.lineNumber}</td>
                              <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                                {template.finalName}
                                {template.finalName !== template.templateName && (
                                  <span className="ml-2 text-xs text-blue-600 italic">
                                    (renommé)
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">{template.items.length}</td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {template.serviceName || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {formatRecurrence(template)}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {template.isValid ? (
                                  <span className="flex items-center gap-1 text-green-700">
                                    <CheckCircle size={16} />
                                    <span>Valide</span>
                                  </span>
                                ) : (
                                  <div className="text-red-700">
                                    <div className="flex items-center gap-1 mb-1">
                                      <XCircle size={16} />
                                      <span>Erreur</span>
                                    </div>
                                    <ul className="text-xs space-y-0.5">
                                      {template.validationErrors.map((error, i) => (
                                        <li key={i}>• {error}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 'importing' && (
              <div className="space-y-6">
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader className="animate-spin text-primary-600 mb-6" size={48} />
                  <p className="text-lg font-semibold text-gray-900 mb-4">
                    Import des templates en cours...
                  </p>
                  <div className="w-full max-w-md">
                    <ProgressBar
                      current={importProgress.current}
                      total={importProgress.total}
                      color="primary"
                      showRatio={true}
                      showPercentage={true}
                    />
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800 text-center">
                    Veuillez ne pas fermer cette fenêtre pendant l'import...
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {!importing && (
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0">
              <div className="flex gap-3">
                <button
                  onClick={handleCloseAttempt}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-all duration-200"
                >
                  Annuler
                </button>
                {step === 'preview' && (
                  <button
                    onClick={handleStartImport}
                    disabled={validTemplates.length === 0}
                    className="flex-1 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Importer ({validTemplates.length} template{validTemplates.length > 1 ? 's' : ''})
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Close Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showCloseConfirmation}
        title="Annuler l'import"
        message="Êtes-vous sûr de vouloir annuler l'import ? Les données saisies seront perdues."
        confirmLabel="Annuler l'import"
        cancelLabel="Continuer"
        onConfirm={onClose}
        onCancel={() => setShowCloseConfirmation(false)}
        variant="warning"
      />
    </>,
    getPortalRoot()
  );
}
